<template>
	<div class="card !shadow-none !border-0">
		<QuizStepper orientation="vertical">
			<StepperPanel v-for="( question, index) in questions" :key="index" :active="currentQuestionIndex === index"
			:header="labels.questionLabel + (index + 1)"
			:class="{ 
				'exam-locked': isCurrentlyExamMode && completedQuestions.has(index),
				'exam-current': isCurrentlyExamMode && index === currentQuestionIndex 
			}"
			
			>

			<template #content="{ prevCallback, nextCallback }">

				<!-- Quiz content - disabled when completed in exam mode -->
				<div class="flex flex-col" :class="{ 
					'opacity-50 pointer-events-none': isCurrentlyExamMode && completedQuestions.has(index) 
				}">
						<InplaceEdit
							:inputProp="{
								propName: 'content',
								inputType: 'quiz',
								content: question
							}"
							method="PUT"
							:href="question._links?.self.href"
							blockTitle="Edit Question"
							:showAssistant="true"
						>
							<span class="font-semibold">{{ question.question }}</span>
						</InplaceEdit>

						<div class="py-5 flex-auto flex flex-col justify-center items-start font-semibold">
							<div v-for="( answer, aIndex) in question.answers" :key="aIndex"
								class="p-4 w-full bg-gray-100">
								<div class="text-md flex">
									<!-- EXAM MODE: Radio button (single selection) -->
									<QuizRadioButton v-if="isCurrentlyExamMode"
										:value="aIndex" 
										v-model="selectedAnswers[index]"
										:inputId="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length"
										:name="'question-' + index" />
									
									<!-- TEST MODE: Checkbox (multiple selection) - DEFAULT -->
									<QuizCheck v-else
										:value="aIndex" 
										v-model="selectedAnswers[index]"
										:inputId="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length"
										:name="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length" />
										
									<label :for="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length"
										class="ml-2 font-normal">
										{{ answer.answer }}
									</label>
								</div>
								<!-- Show feedback: always in test mode, never in exam mode -->
								<div v-if="isCurrentlyTestMode && isAnswerSelected(index, aIndex)">
									<div class="flex items-start gap-2 px-3 font-normal py-3 mt-3 border rounded-md border-success/40 bg-success-100/10 text-dark"
										v-if=" answer.isCorrect ">
										<i
											class="tabler:circle-check !text-[35px] text-success max-w-[40px] !w-[40px] max-h-[40px] !h-[40px] min-w-[40px]"></i>
										<span class="text-sm">{{ answer.explanation }} </span>
									</div>
									<div v-else
										class="flex items-start gap-2 px-3 font-normal py-3 mt-3 border rounded-md border-danger/40 bg-danger-100/10 text-dark">
										<i
											class="tabler:circle-x !text-[35px] text-danger max-w-[40px] !w-[40px] max-h-[40px] !h-[40px] min-w-[40px]"></i>

										<span class="text-sm">{{ answer.explanation }}</span>
									</div>
								</div>
							</div>
						</div>
				</div>
				<div class="flex pt-4 justify-between">
						<!-- Back button: hidden in exam mode -->
						<EwButton v-if="index && isCurrentlyTestMode && !hasFinished" :label="'Back'" type="secondary" :icon="'tabler:arrow-narrow-left'"
							@click="prevCallback" />
						
						<!-- Next button: different labels for different modes -->
						<EwButton v-if=" index < questions.length - 1 " type="success" 
							:label="isCurrentlyExamMode ? 'Next' : 'Next'"
							:iconAppend="'tabler:arrow-narrow-right'" 
							:loading="isSubmittingAnswer"
							@click="() => handleExamNext(nextCallback, index)" />
						
						<!-- Finish button: different labels and styles for different modes -->
							<EwButton v-if=" questions.length - 1 === index " 
								:type="isCurrentlyExamMode ? 'success' : 'success'" 
								:label="isCurrentlyExamMode ? 'Finish' : 'Finish'"
								:iconAppend="isCurrentlyExamMode ? 'tabler:check' : 'tabler:check'" 
								:disabled="hasFinished"
								:loading="isSubmittingAnswer"
							  	@click="isCurrentlyExamMode ? handleFinish() : emits('close')" />
							<EwButton
								v-if="hasFinished"
								
								type="secondary"
								label="Close"
								icon="tabler:x"
								@click="emits('close')"
							/>
					</div>
				</template>
			</StepperPanel>
		</QuizStepper>
		<div ref="swalContainer" class="absolute top-20 right-20 z-2 flex justify-center pointer-events-none"></div>

	</div>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue';
import { messages } from '@/assets/language';
import type {
  configCollectionMode,
  LanguageCodes,
  QuizQuestionsItem,
  SubmittedQuizPayload
} from '@/types';
import InplaceEdit from './InplaceEdit/InplaceEdit.vue';
import { useAppStore } from '@/stores/app-store';
import Swal from 'sweetalert2';

const { currentLng } = useAppStore();
const labels = computed(() => {
  const key = currentLng.value as keyof typeof messages;
  return messages[key] ?? {};
});

type QuizMode = 'quiz' | 'exam';


const props = defineProps<{
  selectedLanguage?: LanguageCodes;
  jsonData: {
    preQuiz: { questions: QuizQuestionsItem[] };
    _links: {
      preQuiz: { href: string };
      preQuizEdit: { href: string };
      preQuizAssesment: { href: string };
    };
  };
  collectionMode?: configCollectionMode | null;
}>();

const emits = defineEmits(['close', 'submit']);

// ✅ Mode dinamike në bazë të collectionMode.id
const currentMode = computed<QuizMode>(() =>
  props.collectionMode?.id === 'audience' ? 'quiz' : 'quiz'
);

console.log('🎯 collectionMode:', props.collectionMode?.id);
console.log('🎯 Current mode:', currentMode.value);

const questions = computed(() => props.jsonData.preQuiz.questions);
const currentQuestionIndex = ref(0);
const hasFinished = ref(false);
const swalContainer = ref<HTMLElement | null>(null);
const completedQuestions = ref<Set<number>>(new Set());
const isSubmittingAnswer = ref(false);

const isCurrentlyExamMode = computed(() => currentMode.value === 'exam');
const isCurrentlyTestMode = computed(() => currentMode.value === 'quiz');

// ✅ Duhet të jetë ref, që v-model të mund të shkruajë në të
const selectedAnswers = ref<Array<Array<number> | number>>(
  props.jsonData.preQuiz.questions.map(() =>
    isCurrentlyExamMode.value ? -1 : []
  )
);

// ✅ Kur ndryshon mode (exam/quiz), rivendos strukturen
watch(currentMode, (newMode) => {
  selectedAnswers.value = props.jsonData.preQuiz.questions.map(() =>
    newMode === 'exam' ? -1 : []
  );
});


// ✅ Check if answer is selected
const isAnswerSelected = (questionIndex: number, answerIndex: number) => {
  if (isCurrentlyExamMode.value) {
    return selectedAnswers.value[questionIndex] === answerIndex;
  } else {
    return (selectedAnswers.value[questionIndex] as number[]).includes(answerIndex);
  }
};

// ✅ Handle “Next” navigation
const handleExamNext = async (nextCallback: () => void) => {
  currentQuestionIndex.value++;
  nextCallback();
};

// ✅ Handle submit / finish
const handleFinish = async () => {
  const payloadQuestions = questions.value.map((question: QuizQuestionsItem, index) => {
    const selected = selectedAnswers.value[index];
	
    if (isCurrentlyExamMode.value) {
      const selectedAnswer = question.answers?.[selected as number];
      return {
        id: question.id,
        answers: selectedAnswer
          ? [
              {
                answerLabel: selectedAnswer.answerLabel ?? '',
                answer: selectedAnswer.answer ?? '',
                isCorrectProvided: 'true'
              }
            ]
          : []
      };
    }

    const selectedIndexes = selected as number[];
    return {
      id: question.id,
      answers: selectedIndexes.map((i) => {
        const a = question.answers?.[i];
        return {
          answerLabel: a?.answerLabel ?? '',
          answer: a?.answer ?? '',
          isCorrectProvided: a?.isCorrect ? 'true' : 'false'
        };
      })
    };
  });

  const finalPayload: SubmittedQuizPayload = {
    mode: currentMode.value,
    questions: payloadQuestions
  };
	isSubmittingAnswer.value = true;
  try {
    const response = await fetch(props.jsonData._links.preQuizAssesment.href, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(finalPayload)
    });

    if (!response.ok) throw new Error(`Request failed: ${response.status}`);

    await Swal.fire({
      icon: 'success',
      title: 'Thank you!',
      text: 'Your answers were submitted successfully.',
      toast: true,
      timer: 2500,
      target: swalContainer.value ?? undefined,
      position: 'center',
      showConfirmButton: false
    });

    emits('submit', finalPayload);
  } catch (err) {
    console.error('❌ Submission error:', err);
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to submit your quiz.',
      toast: true,
      timer: 3000,
      target: swalContainer.value ?? undefined,
      position: 'center',
      showConfirmButton: false
    });
  } finally {
    isSubmittingAnswer.value = false;
    hasFinished.value = true;
  }
};
</script>

<style scoped>
/* .p-stepper {
	flex-basis: 50rem;
} */
</style>
